/**
 * A recording's shadow on disk.
 *
 * Written while a recording runs and read only at the next app start. The chunks in memory stay
 * the source of truth for the live take — nothing here is ever awaited by the recording path, and
 * a store that cannot be opened at all leaves the recording untouched.
 */

/** Everything about a recording except its bytes. One row per recording. */
export interface RecordingHeader {
	videoId: string;
	/** What to call the destination on the offer to send it. */
	label: string;
	/** Where that destination lives, for the card a recovered take lands in. */
	href: string;
	/**
	 * `${orgId}:${userId}` of the session that made it. A rescued take is offered back only to
	 * that identity, because the video row is scoped to its organisation: another one is told the
	 * row does not exist, which is indistinguishable from the row having been swept away.
	 */
	owner: string;
	/** The container `MediaRecorder` negotiated, so a recovered blob is assembled as it was recorded. */
	mimeType: string;
	/** `Date.now()` at start — a wall clock, because it is compared against the server's sweep. */
	startedAtWallMs: number;
}

/** One second of recording, and how far into the recording that second ends. */
export interface ChunkRow {
	videoId: string;
	seq: number;
	blob: Blob;
	/**
	 * Active milliseconds at the moment this chunk arrived. Carried per chunk rather than once per
	 * recording so a record that ends mid-take still describes exactly the bytes that survived.
	 */
	elapsedMs: number;
}

/** The storage the persistence queue writes through. One implementation, plus fakes in the tests. */
export interface RecordingBackend {
	putHeader(header: RecordingHeader): Promise<boolean>;
	putChunk(row: ChunkRow): Promise<boolean>;
	listHeaders(): Promise<RecordingHeader[]>;
	/** The stored chunks, or null when they could not be read — which is not the same as none. */
	readChunks(videoId: string): Promise<ChunkRow[] | null>;
	remove(videoId: string): Promise<void>;
}

const DATABASE_NAME = "msuite-recordings";
const DATABASE_VERSION = 1;
const HEADER_STORE = "recordings";
const CHUNK_STORE = "chunks";

/**
 * How long an unanswered recording stays on the author's disk.
 *
 * The server reaps an `uploading` row at the first nightly sweep more than 24 hours after it was
 * created, so 48 hours covers that sweep's worst case. Past it the row is gone and the record can
 * only ever produce a refusal, which is not worth one to two gigabytes of someone's disk.
 */
export const RECORD_EVICT_AFTER_MS = 48 * 60 * 60 * 1000;

let databasePromise: Promise<IDBDatabase | null> | null = null;

/**
 * The recordings database, opened once. Every failure answers null rather than throwing: durability
 * is a bonus on top of the recording, never a precondition for it, and a private window or a
 * browser with storage switched off has to record exactly as well as one without.
 */
function openDatabase() {
	if (typeof indexedDB === "undefined") return Promise.resolve(null);
	if (databasePromise) return databasePromise;
	databasePromise = new Promise<IDBDatabase | null>((resolve) => {
		try {
			const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
			request.onupgradeneeded = () => {
				const database = request.result;
				if (!database.objectStoreNames.contains(HEADER_STORE)) {
					database.createObjectStore(HEADER_STORE, { keyPath: "videoId" });
				}
				if (!database.objectStoreNames.contains(CHUNK_STORE)) {
					database.createObjectStore(CHUNK_STORE, { keyPath: ["videoId", "seq"] });
				}
			};
			request.onsuccess = () => resolve(request.result);
			request.onerror = () => resolve(null);
			request.onblocked = () => resolve(null);
		} catch {
			resolve(null);
		}
	});
	return databasePromise;
}

/** Resolves with a request's result, rejecting on its error. */
function fromRequest<T>(request: IDBRequest<T>) {
	return new Promise<T>((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () =>
			reject(request.error ?? new Error("The recording store refused a read"));
	});
}

/**
 * Resolves when a transaction has actually committed. A `put` that has succeeded is not yet on
 * disk, and a write reported as landed that is then rolled back is exactly the hole this store
 * exists to avoid.
 */
function committed(transaction: IDBTransaction) {
	return new Promise<void>((resolve, reject) => {
		transaction.oncomplete = () => resolve();
		transaction.onerror = () =>
			reject(transaction.error ?? new Error("The recording store refused a write"));
		transaction.onabort = () =>
			reject(transaction.error ?? new Error("The recording store aborted a write"));
	});
}

/** Every chunk of one recording. Arrays sort above numbers in key order, so `[]` is the upper bound. */
function chunkRange(videoId: string) {
	return IDBKeyRange.bound([videoId], [videoId, []]);
}

export const indexedDbBackend: RecordingBackend = {
	async putHeader(header) {
		const database = await openDatabase();
		if (!database) return false;
		try {
			const transaction = database.transaction(HEADER_STORE, "readwrite");
			transaction.objectStore(HEADER_STORE).put(header);
			await committed(transaction);
			return true;
		} catch {
			return false;
		}
	},

	async putChunk(row) {
		const database = await openDatabase();
		if (!database) return false;
		try {
			const transaction = database.transaction(CHUNK_STORE, "readwrite");
			transaction.objectStore(CHUNK_STORE).put(row);
			await committed(transaction);
			return true;
		} catch {
			return false;
		}
	},

	async listHeaders() {
		const database = await openDatabase();
		if (!database) return [];
		try {
			const transaction = database.transaction(HEADER_STORE, "readonly");
			return await fromRequest<RecordingHeader[]>(transaction.objectStore(HEADER_STORE).getAll());
		} catch {
			return [];
		}
	},

	async readChunks(videoId) {
		const database = await openDatabase();
		if (!database) return [];
		try {
			const transaction = database.transaction(CHUNK_STORE, "readonly");
			const store = transaction.objectStore(CHUNK_STORE);
			return await fromRequest<ChunkRow[]>(store.getAll(chunkRange(videoId)));
		} catch {
			// Null, not an empty array: a record whose chunks cannot be read is unknown, and the
			// caller deletes on "empty".
			return null;
		}
	},

	async remove(videoId) {
		const database = await openDatabase();
		if (!database) return;
		try {
			const transaction = database.transaction([HEADER_STORE, CHUNK_STORE], "readwrite");
			transaction.objectStore(HEADER_STORE).delete(videoId);
			transaction.objectStore(CHUNK_STORE).delete(chunkRange(videoId));
			await committed(transaction);
		} catch {
			// A record that will not delete is left to age out. Reporting it would interrupt an
			// author for something they can neither cause nor fix.
		}
	},
};

/**
 * The playable part of a stored recording: the chunks from the first one to the first gap.
 *
 * A gap is not a shorter recording, it is a broken container — the bytes after it decode against a
 * cluster that never arrived. And chunk zero alone carries the EBML header and the codec
 * initialisation, so a record missing it holds nothing that can be played, however many chunks
 * follow.
 *
 * @param rows - The stored chunks, in any order.
 * @returns The contiguous prefix with the active duration it covers, or null when there is none.
 */
export function reassembleChunks(rows: ChunkRow[]) {
	const bySeq = new Map<number, ChunkRow>();
	for (const row of rows) {
		if (!bySeq.has(row.seq)) bySeq.set(row.seq, row);
	}

	const chunks: Blob[] = [];
	let durationMs = 0;
	for (let seq = 0; bySeq.has(seq); seq++) {
		const row = bySeq.get(seq);
		if (!row) break;
		chunks.push(row.blob);
		durationMs = row.elapsedMs;
	}

	if (chunks.length === 0) return null;
	return { chunks, durationMs };
}
